/* eslint-disable sonarjs/cognitive-complexity -- TODO: Refactor this file */
/* eslint-disable eslint-comments/no-restricted-disable, max-lines -- File is too complex to refactor right now */

import { CustomizedLink } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/CustomizedLink";
import { MentionsExtension } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/Mentions";
import { CustomizedUnderline } from "@circle-react-shared/uikit/TipTap/Extensions/CustomizedUnderline";
import { HighlightedCodeBlock } from "@circle-react-shared/uikit/TipTap/Extensions/HighlightedCodeBlock";
import { CTAExtension } from "@circle-react-shared/uikit/TipTapBlockEditor/CTAExtension";
import { DisableModEnterShortcut } from "@circle-react-shared/uikit/TipTapBlockEditor/DisableModEnterShortcut";
import { EmbedLoaderExtension } from "@circle-react-shared/uikit/TipTapBlockEditor/EmbedLoader";
import { FileLoaderExtension } from "@circle-react-shared/uikit/TipTapBlockEditor/FileLoader";
import { useGiphyPickerModal } from "@circle-react-shared/uikit/TipTapBlockEditor/GiphyPickerModal";
import { useGiphyPickerPopover } from "@circle-react-shared/uikit/TipTapBlockEditor/GiphyPickerModal/GiphyPickerPopover";
import { PaywallExtension } from "@circle-react-shared/uikit/TipTapBlockEditor/PaywallExtension";
import { PollExtension } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension";
import { usePollModal } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal";
import { TrailingNode } from "@circle-react-shared/uikit/TipTapBlockEditor/TrailingNodeExtension";
import { TextModifiersMenu } from "@circle-react-uikit/TipTapBlockEditor/TextModifiersMenu";
import { EntitiesExtension } from "@circle-react/components/shared/uikit/TipTap/Extensions/CustomExtensions/Entities";
import {
  generateLocalSgidToObjectMap,
  getSgidValuesFromHTML,
} from "@circle-react/components/shared/uikit/TipTap/utilities/sgidForEditorContent";
import { FileExtension } from "@circle-react/components/shared/uikit/TipTapBlockEditor/FileExtension";
import { useFileUploadModal } from "@circle-react/components/shared/uikit/TipTapBlockEditor/FileExtension/FileUploadModal";
import { InlineEmojiExtension } from "@circle-react/components/shared/uikit/TipTapBlockEditor/InlineEmojiExtension";
import { useVoiceMessageModal } from "@circle-react/components/shared/uikit/TipTapBlockEditor/VoiceMessageModal";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useIsEmailHubNewsletterPhaseCEnabled } from "@circle-react/hooks/useIsEmailHubNewsletterPhaseCEnabled";
import type {
  AnyExtension,
  EditorEvents,
  Extensions,
  FocusPosition,
} from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import type { Level } from "@tiptap/extension-heading";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Italic from "@tiptap/extension-italic";
import { BulletList } from "@tiptap/extension-list";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import { TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import Typography from "@tiptap/extension-typography";
import {
  Dropcursor,
  Focus,
  Gapcursor,
  UndoRedo as History,
  Placeholder,
} from "@tiptap/extensions";
import { history } from "@tiptap/pm/history";
import type { Editor, JSONContent } from "@tiptap/react";
import { EditorContent, useEditor } from "@tiptap/react";
import { debounce, defer, isFunction } from "lodash";
import type { MutableRefObject, Ref } from "react";
import { useEffect, useImperativeHandle, useState } from "react";

import {
  anchorClassNames,
  cssAnchorProps,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";
import { useBrowserSupportsVoiceRecording } from "@/react/hooks/useBrowserSupportsVoiceRecording";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";
import { useBoolean } from "@/react/hooks/utils/useBoolean";

import { AiCopilotExtension } from "./AiCopilotBlock";
import type { TipTapBlockEditorContext } from "./BlockEditorContext";
import { BlockEditorContext } from "./BlockEditorContext";
import { ALIGNABLE_ELEMENTS, ALIGNMENT_TYPES } from "./constants";
import { useCTASettingsModal } from "./CTAExtension/CTASettingsModal";
import { CustomizedListItem } from "./CustomizedListItemExtension";
import { CustomizedOrderedList } from "./CustomizedOrderedListExtension";
import { CustomizedTable } from "./CustomizedTableExtension";
import { Embeds } from "./EmbedExtension";
import { useEmbedModal } from "./EmbedExtension/EmbedModal";
import { usePlanUpgradeModal } from "./hooks/usePlanUpgradeModal";
import { useShowCopilot } from "./hooks/useShowCopilot";
import { useVideoClipRecorder } from "./hooks/useVideoClipRecorder";
import { Image } from "./ImageExtension";
import { useImagePickerModal } from "./ImageExtension/ImageModal";
import SlashCommands from "./SlashCommands";
import { SmartSelectAll } from "./SmartSelectAll";
import type { EditorApi } from "./types";

// eslint-disable-next-line no-restricted-imports -- pre-existing scss import, migration pending
import "./styles.scss";

interface DisabledMap {
  [key: string]: boolean;
}

const isAnyExtension = (
  extension: AnyExtension | false,
): extension is AnyExtension => Boolean(extension);

export interface TipTapBlockEditorProps {
  additionalExtensions?: Extensions;
  aiCopilotTitle?: string;
  allowBubbleMenuOverflow?: boolean;
  autofocus?: FocusPosition;
  className?: string;
  disabledExtensions?: any[];
  disabledSlashMenuCategories?: any[];
  disabledSlashMenuCommands?: any[];
  disableMentionClicks?: boolean;
  editorApiRef?: Ref<EditorApi>;
  editorClassName?: string;
  /** @deprecated Leaks the raw Tiptap editor. Prefer `editorApiRef`; extend `EditorApi` if you need more operations. */
  editorRef?: MutableRefObject<Editor | null>;
  headingLevels?: Level[];
  inlineAttachments?: any[];
  isAutoLinkEnabled?: boolean;
  isMediaUploadAllowed?: boolean;
  isVideoClipAllowed?: boolean;
  isVoiceMessageAllowed?: boolean;
  isWithinEmailEditor?: boolean;
  maxFileUploadSize?: number;
  onChange?: (json: JSONContent) => void;
  onFocusChange?: (isFocused: boolean) => void;
  onLocalAttachmentsChange?: (value: any) => void;
  onLocalSgidToObjectMapChange?: (value: any) => void;
  openInternalLinksInCurrentTab?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  sgidToObjectMap?: any;
  shouldPreviewFilesAsLinks?: boolean;
  shouldRefetch?: boolean;
  shouldShowTextModifiersMenu?: boolean;
  shouldSupportTables?: boolean;
  showListsInTextModifierMenu?: boolean;
  spaceId?: number;
  textModifierPlacement?: "top" | "bottom";
  toolbars?: any[];
  value?: any;
  variant?: "default" | "compact";
}

export const TipTapBlockEditor = ({
  additionalExtensions = [],
  aiCopilotTitle,
  allowBubbleMenuOverflow = true,
  autofocus = false,
  className,
  disabledExtensions: disabledExtensionsArray = [],
  disabledSlashMenuCategories: disabledSlashMenuCategoriesArray = [],
  disabledSlashMenuCommands: disabledSlashMenuCommandsArray = [],
  disableMentionClicks = false,
  editorApiRef,
  editorClassName,
  editorRef,
  inlineAttachments = [],
  isMediaUploadAllowed = true,
  isVideoClipAllowed = false,
  isVoiceMessageAllowed = false,
  onChange,
  onFocusChange,
  onLocalAttachmentsChange,
  onLocalSgidToObjectMapChange,
  openInternalLinksInCurrentTab = true,
  placeholder,
  readOnly = false,
  sgidToObjectMap = {},
  shouldPreviewFilesAsLinks = false,
  shouldShowTextModifiersMenu = true,
  spaceId,
  textModifierPlacement = "top",
  showListsInTextModifierMenu = false,
  shouldSupportTables = false,
  maxFileUploadSize,
  toolbars,
  value,
  variant = "default",
  shouldRefetch = false,
  headingLevels = [2, 3],
  isAutoLinkEnabled = false,
  isWithinEmailEditor = false,
}: TipTapBlockEditorProps) => {
  const embedModal = useEmbedModal();
  const imagePickerModal = useImagePickerModal();
  const fileUploadModal = useFileUploadModal();
  const ctaSettingsModal = useCTASettingsModal();
  const giphyPickerModal = useGiphyPickerModal();
  const giphyPickerPopover = useGiphyPickerPopover();
  const isCircleV4PopoverEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_popover_enabled",
  );
  const isPaywallExtensionEnabled = useIsEmailHubNewsletterPhaseCEnabled();
  const { isCircleV4 } = useCircleV4();
  const pollModal = usePollModal();
  const voiceMessageModal = useVoiceMessageModal();
  const { nativeVideoCaptureInputProps, showVideoClipRecorder } =
    useVideoClipRecorder();
  const [localSgidToObjectMap, setLocalSgidToObjectMap] = useState<any>({});
  const [localInlineAttachments, setLocalInlineAttachments] = useState<any>([]);
  const [isFocused, , setIsFocused] = useBoolean(false);
  const { shouldShowCopilot: canUseCopilot, shouldShowPlanUpgradeModal } =
    useShowCopilot();
  const { openUpgradeModal } = usePlanUpgradeModal();
  const doesBrowserSupportVoiceRecording = useBrowserSupportsVoiceRecording();
  const [
    disabledExtensions,
    disabledSlashMenuCategories,
    disabledSlashMenuCommands,
  ]: DisabledMap[] = [
    disabledExtensionsArray,
    disabledSlashMenuCategoriesArray,
    disabledSlashMenuCommandsArray,
  ].map(array => Object.fromEntries(array.map(key => [key, true])));

  disabledExtensions["ai_copilot"] =
    disabledExtensions["ai_copilot"] ||
    !(canUseCopilot || shouldShowPlanUpgradeModal) ||
    readOnly;

  disabledExtensions["voice_message"] =
    disabledExtensions["voice_message"] ||
    !isVoiceMessageAllowed ||
    !doesBrowserSupportVoiceRecording;

  disabledSlashMenuCommands["voice_message"] =
    disabledSlashMenuCommands["voice_message"] ||
    !isVoiceMessageAllowed ||
    !doesBrowserSupportVoiceRecording;

  disabledExtensions["video_clip"] =
    disabledExtensions["video_clip"] || !isVideoClipAllowed;

  disabledSlashMenuCommands["video_clip"] =
    disabledSlashMenuCommands["video_clip"] || !isVideoClipAllowed;

  // Disable media upload extensions when not allowed
  if (!isMediaUploadAllowed) {
    disabledExtensions["file"] = true;
    disabledExtensions["image"] = true;
    disabledExtensions["file_loader"] = true;
    disabledExtensions["video_clip"] = true;
    disabledSlashMenuCommands["file"] = true;
    disabledSlashMenuCommands["image"] = true;
    disabledSlashMenuCommands["video"] = true;
    disabledSlashMenuCommands["audio"] = true;
    disabledSlashMenuCommands["pdf"] = true;
    disabledSlashMenuCommands["giphy"] = true;
    disabledSlashMenuCommands["video_clip"] = true;
  }

  const addToLocalSgidToObjectMap = ({
    sgid,
    object,
  }: {
    sgid: string;
    object: any;
  }) => {
    setLocalSgidToObjectMap((prevState: any) => ({
      ...prevState,
      [sgid]: object,
    }));

    if (isFunction(onLocalSgidToObjectMapChange)) {
      const newMap = { ...localSgidToObjectMap, [sgid]: object };
      onLocalSgidToObjectMapChange(newMap);
    }
  };

  const addToLocalInlineAttachments = (attachment: any) => {
    setLocalInlineAttachments((prevState: any) => {
      if (prevState.some((a: any) => a.signed_id === attachment.signed_id)) {
        return prevState;
      }
      const newState = [...prevState, attachment];
      if (isFunction(onLocalAttachmentsChange)) {
        onLocalAttachmentsChange(newState);
      }
      return newState;
    });
  };

  const showImagePickerModal = (args: any) =>
    imagePickerModal.show({
      ...args,
      addToLocalInlineAttachments,
      isWithinEmailEditor,
    });

  const showGiphyPickerModal = (args: any) => {
    if (isCircleV4PopoverEnabled) {
      giphyPickerPopover.show({
        ...args,
        addToLocalInlineAttachments,
      });
      return;
    }
    void giphyPickerModal.show({
      ...args,
      addToLocalInlineAttachments,
    });
  };

  const showEmbedModal = (args: any) =>
    embedModal.show({ ...args, addToLocalSgidToObjectMap });

  const showFileUploadModal = (args: any) =>
    fileUploadModal.show({
      ...args,
      maxFileUploadSize,
      addToLocalInlineAttachments,
      addToLocalSgidToObjectMap,
    });

  const showPollModal = (args: any) =>
    pollModal.show({ ...args, addToLocalSgidToObjectMap });

  const showCTASettingsModal = (args: any) =>
    ctaSettingsModal.show({
      ...args,
      addToLocalSgidToObjectMap,
      isWithinEmailEditor,
    });

  const showVoiceMessageModal = (args: any) =>
    voiceMessageModal.show({
      ...args,
      addToLocalInlineAttachments,
    });

  const extensions: Extensions = [
    !disabledExtensions["blockquote"] && Blockquote,
    Bold,
    Italic,
    Strike,
    CustomizedUnderline,
    Document,
    Focus,
    Gapcursor,
    HardBreak,
    shouldSupportTables && CustomizedTable.configure({ resizable: false }),
    shouldSupportTables && TableCell,
    shouldSupportTables && TableHeader,
    shouldSupportTables && TableRow,
    !disabledExtensions["code_block"] && HighlightedCodeBlock,
    !disabledExtensions["headings"] &&
      Heading.configure({
        levels: headingLevels,
      }),
    History,
    BulletList,
    CustomizedListItem,
    CustomizedOrderedList,
    Paragraph,
    Text,
    isWithinEmailEditor &&
      TextAlign.configure({
        types: Array.from(ALIGNABLE_ELEMENTS),
        alignments: [
          ALIGNMENT_TYPES.LEFT,
          ALIGNMENT_TYPES.RIGHT,
          ALIGNMENT_TYPES.CENTER,
        ],
      }),
    Typography,
    Highlight.configure({
      multicolor: true,
    }),
    CustomizedLink.configure({
      openInternalLinksInCurrentTab,
      openOnClick: false,
      autolink: isAutoLinkEnabled,
    }),
    Placeholder.configure({
      placeholder,
      emptyEditorClass: "is-block-editor-empty text-light",
      emptyNodeClass: "is-empty",
      showOnlyWhenEditable: false,
      showOnlyCurrent: false,
    }),
    !disabledExtensions["horizontal_rule"] && HorizontalRule,
    !disabledExtensions["file"] && FileExtension,
    InlineEmojiExtension.configure({
      useCompassSuggestionMenu: isCircleV4,
    }),
    EmbedLoaderExtension,
    !disabledExtensions["file_loader"] && FileLoaderExtension,
    !disabledExtensions["ai_copilot"] && AiCopilotExtension,
    !disabledExtensions["slash_commands"] &&
      SlashCommands.configure({
        showEmbedModal,
        showImagePickerModal,
        showFileUploadModal,
        showGiphyPickerModal,
        showPollModal,
        showCTASettingsModal,
        showVideoClipRecorder,
        showVoiceMessageModal,
        disabledExtensions,
        disabledSlashMenuCategories,
        disabledSlashMenuCommands,
        shouldShowPlanUpgradeModal,
        openUpgradeModal,
        headingLevels,
        useCompassSuggestionMenu: isCircleV4,
      }),
    !disabledExtensions["embeds"] && Embeds,
    !disabledExtensions["image"] && Image,
    !disabledExtensions["mention"] &&
      MentionsExtension.configure({
        useCompassSuggestionMenu: isCircleV4,
      }),
    !disabledExtensions["entities"] &&
      EntitiesExtension.configure({
        useCompassSuggestionMenu: isCircleV4,
      }),
    !disabledExtensions["poll"] && PollExtension,
    !disabledExtensions["cta"] && CTAExtension,
    isWithinEmailEditor &&
      isPaywallExtensionEnabled &&
      !disabledExtensions["paywall"] &&
      PaywallExtension,
    !readOnly && TrailingNode,
    DisableModEnterShortcut,
    SmartSelectAll,
    Dropcursor.configure({
      width: 2,
    }),
    ...additionalExtensions,
  ].filter(isAnyExtension);

  useEffect(() => {
    if (isFunction(onFocusChange)) {
      onFocusChange(isFocused);
    }
  }, [isFocused, onFocusChange]);

  const onUpdate = ({ editor }: EditorEvents["update"]) => {
    const json = editor.getJSON();
    onChange && onChange(json);
  };

  const debouncedOnUpdate = debounce(onUpdate, 200);

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions,
    autofocus,
    onUpdate: debouncedOnUpdate,
    onFocus: () => {
      setIsFocused(true);
    },
    onBlur: () => {
      setIsFocused(false);
    },
    content: value,
    editable: !readOnly,
    editorProps: {
      transformPastedHTML(html: any) {
        const sgids: string[] = getSgidValuesFromHTML(html);
        if (sgids.length) {
          void generateLocalSgidToObjectMap({
            sgids,
            setLocalSgidToObjectMap,
          });
        }
        return html;
      },
      attributes: {
        focus: "",
        class: classNames(
          "z-0 max-w-none whitespace-pre-wrap border-none bg-transparent text-dark ring-0",
          {
            "!scale-100 text-sm leading-sm": variant === "compact",
          },
          anchorClassNames("circle-block-editor"),
          variant === "compact" &&
            legacyClassNames("circle-block-editor--compact"),
          editorClassName,
        ),
        "data-readonly": readOnly ? "true" : "false",
        translate: readOnly ? "yes" : "no",
        ...cssAnchorProps({ anchor: "rich-text-content" }),
      },
    },
  });

  if (editorRef) {
    editorRef.current = editor;
  }

  useImperativeHandle(
    editorApiRef,
    () => ({
      blur: () => editor?.commands.blur(),
      focus: () => editor?.commands.focus("end", { scrollIntoView: false }),
      isEmpty: () => editor?.isEmpty ?? true,
      reset: () => {
        if (!editor) return;
        editor.commands.clearContent(false);
        // Re-register the history extension to drop the undo stack
        editor.unregisterPlugin("history");
        editor.registerPlugin(history());
        setLocalSgidToObjectMap({});
        setLocalInlineAttachments([]);
      },
      setContent: (content, options) => {
        if (!editor) return;
        editor.commands.setContent(content, { emitUpdate: true });
        const sgidToObjectMap = options?.sgidToObjectMap ?? {};
        const inlineAttachments = options?.inlineAttachments ?? [];
        setLocalSgidToObjectMap(sgidToObjectMap);
        setLocalInlineAttachments(inlineAttachments);
        onLocalSgidToObjectMapChange?.(sgidToObjectMap);
        onLocalAttachmentsChange?.(inlineAttachments);
      },
    }),
    [editor, onLocalSgidToObjectMapChange, onLocalAttachmentsChange],
  );

  useEffect(() => {
    shouldRefetch &&
      readOnly &&
      editor &&
      defer(() => editor.commands.setContent(value, { emitUpdate: false })); // Re rending editor after the initial value is changed on condition
  }, [editor, value, shouldRefetch, readOnly]);

  useEffect(() => {
    if (editor && readOnly === editor.isEditable) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  const handleUpload = (files: FileList) => {
    if (disabledExtensions["file"] || !editor) {
      return;
    }

    editor
      .chain()
      .focus()
      .insertContent(
        Array.from(files).map(file => ({
          type: "file-loader",
          attrs: { file },
        })),
      )
      .run();
  };

  const contextValue: TipTapBlockEditorContext = {
    sgidToObjectMap,
    inlineAttachments,
    localSgidToObjectMap,
    addToLocalSgidToObjectMap,
    localInlineAttachments,
    addToLocalInlineAttachments,
    spaceId,
    aiCopilotTitle,
    shouldPreviewFilesAsLinks,
    type: "block",
    disableMentionClicks,
    isWithinEmailEditor,
  };

  return (
    <BlockEditorContext.Provider value={contextValue}>
      {editor && shouldShowTextModifiersMenu && (
        <TextModifiersMenu
          shouldShowCopilot={!disabledExtensions["ai_copilot"]}
          editor={editor}
          headingLevels={headingLevels}
          placement={textModifierPlacement}
          showListsInTextModifierMenu={showListsInTextModifierMenu}
          allowBubbleMenuOverflow={allowBubbleMenuOverflow}
        />
      )}
      {toolbars &&
        toolbars.map(({ portalId, component: ToolbarComponent }) => (
          <ToolbarComponent
            key={`editor-toolbar-for-${portalId}`}
            editor={editor}
            isEditorFocused={isFocused}
            portalId={portalId}
            showImagePickerModal={showImagePickerModal}
            showFileUploadModal={showFileUploadModal}
            showGiphyPickerModal={showGiphyPickerModal}
            showPollModal={showPollModal}
            showVideoClipRecorder={showVideoClipRecorder}
            showVoiceMessageModal={showVoiceMessageModal}
            disabledExtensions={disabledExtensions}
          />
        ))}
      {giphyPickerPopover.element}
      {nativeVideoCaptureInputProps && (
        <input {...nativeVideoCaptureInputProps} />
      )}
      <EditorContent
        onPaste={e => {
          if (isMediaUploadAllowed && e.clipboardData?.files?.length > 0) {
            handleUpload(e.clipboardData.files);
          }
        }}
        onDrop={e => {
          if (!editor || !isMediaUploadAllowed) return null;
          e.preventDefault();
          const dropCoords = { left: e.clientX, top: e.clientY };
          const dropPos = editor.view.posAtCoords(dropCoords);
          if (dropPos) {
            return Array.from(e.dataTransfer.files).forEach(file => {
              editor
                .chain()
                .focus()
                .insertContentAt(dropPos.pos, {
                  type: "file-loader",
                  attrs: { file },
                })
                .run();
            });
          }
        }}
        className={className}
        editor={editor}
        data-testid="tip-tap-editor-content"
      />
    </BlockEditorContext.Provider>
  );
};
